/* eslint-disable @repo/internal/styles/consistent-style-ordering */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import LinkIcon from '@atlaskit/icon/glyph/link';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import ClickCopyAnchor from '../click-copy-anchor';

const sectionLinkStyles = css({
  a: {
    [media.above.md]: {
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
      marginInlineStart: '-2rem',
      opacity: 0,
      position: 'absolute',
      transform: 'translateX(4px)',
      transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
      '&:focus': {
        opacity: 1,
      },
    },
  },
  '&:hover': {
    a: {
      opacity: 1,
      transitionDuration: '0.1s',
      transform: 'none',
    },
  },
  /**
   * We want to apply vertical space between each section, so that content within a section is visually grouped, and sections
   * have visual separation from each other. We can do this by applying a `margin-top` to `SectionLink`, however
   * mainContentStyles already has a `padding-top: 32px;` which creates too much vertical space on the first element of MDX content
   * As these section headers are wrapped in <div>s the standard CSS reset doesn't apply, so we handle the first-child differently here.
   */
  marginTop: token('space.200', '16px'),
  ':first-child:is(div), style:first-child + :is(div)': {
    marginTop: token('space.0', '0px'),
  },
});

type SectionLinkProps = {
  id: string;
  children: any;
  clickToCopyTestId?: string;
  label: string;
  css?: any;
};

const sectionLinkAnchorStyles = xcss({
  display: 'flex',
  position: 'relative',
  flexShrink: '0',
  alignSelf: 'start',
});

const SectionLink = ({
  id,
  clickToCopyTestId,
  children,
  label,
}: SectionLinkProps) => (
  <div id={id} css={sectionLinkStyles}>
    <Inline space="space.025" alignBlock="center">
      <Box xcss={sectionLinkAnchorStyles}>
        <ClickCopyAnchor href={`#${id}`} testId={clickToCopyTestId}>
          <LinkIcon label={label} />
        </ClickCopyAnchor>
      </Box>
      {children}
    </Inline>
  </div>
);

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default SectionLink;
